import { useCallback, useEffect, useRef, useState } from "react";
import { needsResync, playback, type StationState, type TrackDoc } from "radio-shared";
import { api } from "../api";
import { loadYouTubeApi, youTubeErrorMessage, type YTPlayer } from "./useYouTubeApi";

export type PlayerStatus = "off" | "loading" | "playing" | "silent" | "blocked" | "error";

/** Conteneur du player YouTube — présent dans le DOM, invisible : on ne veut que le son. */
export const YT_CONTAINER_ID = "yt-host";

/**
 * Le lecteur de la station.
 *
 * Il ne décide de rien : il suit `nowPlaying` et se recale en continu sur
 * l'horloge du serveur. C'est ce qui fait que trois personnes qui ouvrent la
 * page à trente secondes d'intervalle entendent la même seconde du même son.
 *
 * Deux moteurs derrière la même logique :
 * - YouTube, via le player officiel dans une iframe — rien n'est téléchargé,
 *   c'est le navigateur de l'auditeur qui parle à YouTube (et lui n'est pas
 *   bloqué, contrairement à nos IP de datacenter) ;
 * - un <audio> pour nos mp3 : liens SoundCloud importés, fichiers déposés,
 *   et les dédicaces au micro.
 *
 * Un seul est actif à la fois, l'autre est mis en pause.
 */
export function usePlayer(state: StationState | null, current: TrackDoc | null, serverNow: () => number) {
  const audio = useRef<HTMLAudioElement | null>(null);
  const yt = useRef<YTPlayer | null>(null);
  const loadedTrack = useRef<string | null>(null);
  const ducked = useRef(false);

  const [on, setOn] = useState(false);
  const [status, setStatus] = useState<PlayerStatus>("off");
  const [error, setError] = useState<string | null>(null);
  const [positionMs, setPositionMs] = useState(0);

  if (!audio.current && typeof Audio !== "undefined") {
    audio.current = new Audio();
    audio.current.preload = "auto";
  }

  const nowPlaying = state?.nowPlaying ?? null;
  const isYouTube = current?.kind === "youtube";

  // Ce qui identifie réellement « le son à jouer ». Des primitives, pour que les
  // effets ne se redéclenchent que sur un vrai changement.
  const trackId = nowPlaying?.trackId ?? null;
  const kind = current && current.id === trackId ? current.kind : null;
  const videoId = current && current.id === trackId ? current.videoId : undefined;

  /** Demande l'avance ; le serveur arbitre si plusieurs clients le font ensemble. */
  const requestAdvance = useCallback(async (expected: string | null) => {
    try {
      await api.advance(expected);
    } catch {
      // sans conséquence : le tick suivant réessaiera
    }
  }, []);

  /**
   * Prépare le player YouTube au premier besoin, puis le réutilise.
   *
   * Trois pièges, tous trois constatés en vrai :
   *
   * 1. **Créer le player AVEC sa vidéo.** Sans `videoId`, l'API construit une
   *    iframe sur `youtube.com/embed/` — chemin vide — et YouTube répond
   *    aussitôt l'erreur 2 (« paramètre invalide »), affichée à l'utilisateur
   *    comme « Lien YouTube invalide » alors que le lien est bon.
   *
   * 2. **Ne pas passer `origin`.** Bien que l'IFrame API le documente, le player
   *    ne rappelle alors jamais `onReady` : la poignée de main postMessage
   *    n'aboutit pas et rien ne joue, sans même remonter d'erreur.
   *
   * 3. **Toujours partir d'un div neuf.** `YT.Player` *remplace* l'élément qu'on
   *    lui donne par son iframe, en lui laissant son id. Réutiliser cet id
   *    revient à construire un player par-dessus une iframe existante, ce que
   *    YouTube refuse — encore l'erreur 2.
   */
  const ensureYouTube = useCallback(
    async (videoId: string, startSeconds: number): Promise<{ player: YTPlayer; fresh: boolean }> => {
      if (yt.current) return { player: yt.current, fresh: false };

      const YT = await loadYouTubeApi();
      const host = document.getElementById(YT_CONTAINER_ID);
      if (!host) throw new Error("conteneur du player absent");

      // Le div est créé ici, hors de React : c'est YouTube qui va le consommer,
      // et React ne doit pas espérer le retrouver.
      host.replaceChildren();
      const slot = document.createElement("div");
      host.appendChild(slot);

      const player = await new Promise<YTPlayer>((resolve, reject) => {
        let settled = false;

        const created = new YT.Player(slot, {
          videoId,
          playerVars: {
            autoplay: 1,
            controls: 0,
            disablekb: 1,
            // sur iOS, sans ça le player passe en plein écran de force
            playsinline: 1,
            modestbranding: 1,
            rel: 0,
            // on entre au milieu du morceau, là où en est la station
            start: Math.max(0, Math.floor(startSeconds)),
          },
          events: {
            onReady: () => {
              if (settled) return;
              settled = true;
              yt.current = created;
              resolve(created);
            },
            onError: (e) => {
              const message = youTubeErrorMessage(e.data);
              if (!settled) {
                settled = true;
                reject(new Error(message));
                return;
              }
              // Après coup : la vidéo en cours est injouable ici. On le dit sans
              // faire avancer la station — l'incident peut être purement local,
              // et couper le son de tout le monde serait pire.
              setError(message);
              setStatus("error");
            },
          },
        });

        setTimeout(() => {
          if (settled) return;
          settled = true;
          reject(new Error("Le player YouTube ne répond pas"));
        }, 15_000);
      });

      return { player, fresh: true };
    },
    [],
  );

  // Les callbacks du player sont créés une fois pour toutes : ils ont besoin de
  // lire le morceau courant sans être recréés à chaque changement.
  const nowPlayingRef = useRef(nowPlaying);
  nowPlayingRef.current = nowPlaying;

  /**
   * Charge le bon son dans le bon moteur quand le morceau change.
   *
   * Les dépendances sont volontairement **primitives** — l'identifiant du
   * morceau, son type, sa vidéo — et non les objets `nowPlaying` / `current`,
   * recréés à chaque snapshot et à chaque render. Sinon cet effet se
   * réexécutait une fois par seconde (le tick appelle `setPositionMs`), son
   * nettoyage annulait le chargement en cours juste après avoir marqué le
   * morceau comme chargé, et plus aucune tentative n'avait lieu : le player
   * restait bloqué sur le morceau précédent. C'était le bug du skip sans effet.
   */
  useEffect(() => {
    if (!on) return;

    const el = audio.current;
    const nowPlayingNow = nowPlayingRef.current;

    if (!nowPlayingNow || !trackId || !kind) {
      el?.pause();
      yt.current?.pauseVideo();
      loadedTrack.current = null;
      setStatus("silent");
      return;
    }
    if (loadedTrack.current === trackId) return;

    loadedTrack.current = trackId;
    setStatus("loading");
    setError(null);

    let cancelled = false;

    void (async () => {
      try {
        const startMs = playback(nowPlayingNow, serverNow()).positionMs;

        if (kind === "youtube" && videoId) {
          el?.pause();
          const { player, fresh } = await ensureYouTube(videoId, startMs / 1000);
          if (cancelled) return;
          player.setVolume(ducked.current ? 15 : 100);
          // un player tout juste créé porte déjà la bonne vidéo au bon endroit :
          // le recharger le ferait repartir pour rien
          if (!fresh) {
            player.loadVideoById({ videoId, startSeconds: startMs / 1000 });
          }
          player.playVideo();
        } else {
          yt.current?.pauseVideo();
          if (!el) throw new Error("lecteur audio indisponible");
          const { url } = await api.audioUrl(trackId);
          if (cancelled) return;
          el.src = url;
          el.currentTime = startMs / 1000;
          el.volume = ducked.current ? 0.15 : 1;
          await el.play();
        }

        if (!cancelled) setStatus("playing");
      } catch (err) {
        if (cancelled) return;
        // Sans ça, un morceau dont le chargement a raté ne serait jamais retenté.
        loadedTrack.current = null;
        // Un player qui n'a jamais répondu est inutilisable : on le jette pour
        // qu'un prochain essai en reconstruise un propre.
        if (!yt.current) {
          document.getElementById(YT_CONTAINER_ID)?.replaceChildren();
        }

        // Un play() refusé signifie presque toujours que le navigateur exige un
        // geste de l'utilisateur.
        if (err instanceof DOMException && err.name === "NotAllowedError") {
          setStatus("blocked");
        } else {
          setError(err instanceof Error ? err.message : "Lecture impossible");
          setStatus("error");
        }
      }
    })();

    return () => {
      cancelled = true;
      // Le chargement a été interrompu avant d'aboutir : ne pas laisser ce
      // morceau marqué comme chargé, sinon il ne sera plus jamais tenté.
      if (loadedTrack.current === trackId) loadedTrack.current = null;
    };
  }, [on, trackId, kind, videoId, serverNow, ensureYouTube]);

  /** Le battement de la radio : avancer quand c'est fini, recaler quand ça dérive. */
  useEffect(() => {
    if (!on) return;

    const tick = () => {
      const playing = nowPlayingRef.current;
      if (!playing) return;

      const { positionMs: target, finished } = playback(playing, serverNow());
      setPositionMs(target);

      if (finished) {
        void requestAdvance(playing.trackId);
        return;
      }

      if (isYouTube) {
        const player = yt.current;
        if (!player) return;
        const drift = player.getCurrentTime() * 1000 - target;
        if (needsResync(drift)) player.seekTo(target / 1000, true);
        return;
      }

      const el = audio.current;
      if (!el) return;
      const drift = el.currentTime * 1000 - target;
      if (needsResync(drift) && el.readyState >= 2) el.currentTime = target / 1000;
    };

    const timer = setInterval(tick, 1000);
    tick();
    return () => clearInterval(timer);
  }, [on, trackId, serverNow, requestAdvance, isYouTube]);

  /** Un mp3 peut finir avant l'horloge (durée mesurée un cheveu plus courte). */
  useEffect(() => {
    const el = audio.current;
    if (!el) return;
    const onEnded = () => void requestAdvance(nowPlayingRef.current?.trackId ?? null);
    el.addEventListener("ended", onEnded);
    return () => el.removeEventListener("ended", onEnded);
  }, [requestAdvance]);

  /** Allumer la radio. Doit venir d'un clic : les navigateurs interdisent l'autoplay. */
  const turnOn = useCallback(() => {
    setOn(true);
    setStatus(nowPlaying ? "loading" : "silent");
    // la file a pu se remplir sans personne pour l'entendre démarrer
    void api.kick().catch(() => undefined);
  }, [nowPlaying]);

  const turnOff = useCallback(() => {
    audio.current?.pause();
    yt.current?.pauseVideo();
    loadedTrack.current = null;
    setOn(false);
    setStatus("off");
  }, []);

  /** Baisse le son de la musique pendant qu'on parle dans le micro. */
  const setDucked = useCallback((next: boolean) => {
    ducked.current = next;
    if (audio.current) audio.current.volume = next ? 0.15 : 1;
    yt.current?.setVolume(next ? 15 : 100);
  }, []);

  return { on, status, error, positionMs, turnOn, turnOff, setDucked };
}
